import * as React from 'react';

// Necesario porque react-color no es un esmodule
import * as pkg from 'react-color';

import { Binding, Result } from 'core/infrastructure';

import { FormComponent } from './Form';
import ErrorBlock from './ErrorBlock';
import { formatAsElementId } from './Utils';
import * as Colors from '../graphics/Colors';

declare type ColorFormat = 'hex' | 'rgb';

interface ColorPickerProps<T> extends React.HTMLProps<ColorPicker<T>> {
	linkTo: Binding.PropertyPath<T>;
	boundValue?: string;
	label?: string;
	format: ColorFormat;
	formId?: string;
	onValueChange?: (value: string) => void;

	// Permite configurar si al desplegarse el ColorPicker se muestra
	// como un popover o amplia el tamaño del padre. Es necesario porque
	// si se muestra en un diálogo, hace falta ponerlo como inline
	// para que crezca el cuerpo del diálogo y se pueda acceder al
	// selector de color sin necesidad de hacer scroll
	dropdown?: 'inline' | 'popover';
}

interface ColorPickerState {
	hexColor?: string;
	displayPicker?: boolean;
	errorMsg?: string;
}

export class ColorPicker<T> extends React.Component<ColorPickerProps<T>, ColorPickerState> implements FormComponent {

	constructor(props: ColorPickerProps<T>) {
		super(props);

		if (typeof props.boundValue !== 'string')
			throw new Error('ColorPicker como boundValue esperaba un string');

		this.state = this.createStateFromProps(props);

		this.onOpenColorPicker = this.onOpenColorPicker.bind(this);
		this.onCloseColorPicker = this.onCloseColorPicker.bind(this);
		this.onColorChange = this.onColorChange.bind(this);
	}

	createStateFromProps(props: ColorPickerProps<T>) {
		// Pongo un valor por defecto si no me llega
		const value = props.boundValue || (this.props.format === 'rgb' ? '186, 205, 226' : '#bacde2');
		return {
			hexColor: this.props.format === 'rgb' ? Colors.rgbToHex(value) : value,
			displayPicker: false
		};
	}

	getPath(): string {
		return Binding.propertyPathToString(this.props.linkTo);
	}

	showErrors(errors: string[]): void {
		this.setState({ errorMsg: errors.join(' ') });
	}

	getValue(): Result<string> {
		this.setState({ errorMsg: '' });
		const newColor = this.props.format === 'rgb'
			? Colors.hexToRgb(this.state.hexColor || '#bacde2')
			: this.state.hexColor || '';
		return Result.ok(newColor);
	}

	private onOpenColorPicker() {
		this.setState({
			displayPicker: !this.state.displayPicker,
		}, () => {

			setTimeout(() => {
				const element = document.getElementById('color-picker-popover');
				element?.scrollIntoView({
					behavior: 'auto',
					block: 'center',
					inline: 'center'
				});
			});

		});
	}

	private onCloseColorPicker() {
		this.setState({
			displayPicker: false
		});
	}

	private onColorChange({ hex }: { hex: string }) {
		this.setState({
			hexColor: hex,
			displayPicker: true
		}, () => this.props.onValueChange && this.props.onValueChange(hex));
	}

	render() {

		const position = this.props.dropdown === 'inline' ? 'relative' : 'absolute';
		const popover: React.CSSProperties = {
			position: position,
			zIndex: 3
		};

		const thumb: React.CSSProperties = {
			background: this.state.hexColor,
			width: '18px',
			height: '18px'
		};

		const inputId = formatAsElementId(this.getPath(), this.props.formId || '');

		// Lo que hace es poner un div fixed en toda la pantalla para cuando
		// se pincha fuera del color picker se pueda cerrar
		const cover: React.CSSProperties = {
			position: 'fixed',
			top: '0px',
			right: '20px', // Para poder usar la barra de scroll
			bottom: '0px',
			left: '0px',
			zIndex: -1
		};

		// Estos colores están sacados de los colores que eligió Desirée para la base de datos de ejemplo
		const presetColors = [
			'#A90404', '#A63C08',
			'#FD7C7C', '#FEAEAE',
			'#F58851', '#F0B514',
			'#FCE4A1',
			'#00BA4C', '#54DA87',
			'#5DB6FD', '#8BCBFE', '#DCEFFF',
			'#D6BCFA',
			'#000000', '#AAB5C5', '#FFFFFF',
		];

		return (
			<div className='form-group'>
				<label className='col-sm-4 control-label'>{this.props.label || ''}</label>
				<div className='col-sm-8'>
					<div className='input-group'>
						<span className='input-group-addon' style={{ cursor: 'pointer' }} onClick={this.onOpenColorPicker}>
							<div style={thumb} />
						</span>
						<input id={inputId} type='text' className='form-control' onClick={this.onOpenColorPicker} readOnly value={this.state.hexColor} style={{ background: 'white', cursor: 'pointer' }} />
						<span className='input-group-btn'>
							<button type='button' className='btn btn-default' onClick={this.onOpenColorPicker}><i className='text-info fa fa-paint-brush'></i></button>
						</span>
					</div>
					{this.state.displayPicker ?
						<div id='color-picker-popover' style={popover}>
							<div id='cover' style={cover} onClick={this.onCloseColorPicker} />
							<pkg.SketchPicker color={this.state.hexColor} onChange={this.onColorChange} presetColors={presetColors} />
						</div>
						: null}
				</div>
				<ErrorBlock msg={this.state.errorMsg} />
			</div>
		);
	}
}

export type ColorPickerCtor<T> = new (props: ColorPickerProps<T>) => React.Component<ColorPickerProps<T>, ColorPickerState>;
